import { motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { Tooltip } from "./Tooltip";
import { UnderlineOnView } from "./UnderlineOnView";

/** Animated background for the Why Forward section */
const WhyForwardBackground = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Dot grid */}
    <svg
      className="absolute inset-0 w-full h-full opacity-[0.07]"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <pattern
          id="whyDots"
          x="0"
          y="0"
          width="28"
          height="28"
          patternUnits="userSpaceOnUse"
        >
          <circle cx="2" cy="2" r="1.2" fill="#111118" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#whyDots)" />
    </svg>
    {/* Animated gradient blobs */}
    <motion.div
      className="absolute w-[600px] h-[600px] rounded-full"
      style={{
        background:
          "radial-gradient(circle, rgba(255,77,46,0.14) 0%, rgba(255,77,46,0.04) 45%, transparent 70%)",
        top: "-15%",
        right: "-10%",
      }}
      animate={{ x: [0, -40, 0], y: [0, 30, 0] }}
      transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute w-[500px] h-[500px] rounded-full"
      style={{
        background:
          "radial-gradient(circle, rgba(255,225,77,0.12) 0%, rgba(255,225,77,0.03) 45%, transparent 70%)",
        bottom: "-10%",
        left: "-8%",
      }}
      animate={{ x: [0, 30, 0], y: [0, -25, 0] }}
      transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
    />
    {/* Diagonal accent lines */}
    <svg
      className="absolute inset-0 w-full h-full"
      xmlns="http://www.w3.org/2000/svg"
    >
      {[0, 1, 2].map((i) => (
        <motion.line
          key={i}
          x1={`${20 + i * 30}%`}
          y1="0%"
          x2={`${5 + i * 30}%`}
          y2="100%"
          stroke="rgba(255,77,46,0.06)"
          strokeWidth="1"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, delay: i * 0.3 }}
        />
      ))}
    </svg>
  </div>
);

/**
 * Real architecture requirements from this project.
 * Shown inside the "the challenge" tooltip so prospects grasp the depth.
 */
const CHALLENGE_ITEMS = [
  "Turning raw data into compelling narratives — at scale",
  "Personalization that feels hand-crafted, not templated",
  "Beautiful, interactive narratives on every device",
  "Charts, animations, and media that just work — everywhere",
  "One personal link per recipient, sent from your email platform",
  "Mobile-first design that people actually want to swipe through",
  "Engagement tracking so you know what resonates",
  "Quality controls that catch issues before your audience does",
];

export const NotDIYSection = () => (
  <section className="bg-white py-24 md:py-32 px-8 md:px-16 lg:px-20 relative overflow-x-clip">
    <WhyForwardBackground />
    <div className="max-w-4xl mx-auto text-center relative z-10">
      <Reveal>
        <span className="text-fwd-red text-xs font-bold tracking-[0.2em] uppercase block mb-6">
          Why Forward
        </span>
      </Reveal>

      <Reveal delay={0.1}>
        <h2 className="font-display text-[clamp(2.2rem,4.2vw,3.6rem)] font-normal leading-[1.15] text-fwd-ink mb-8">
          Start sending <UnderlineOnView delay={2}>now</UnderlineOnView>.
          <br className="hidden md:block" /> Skip the DIY build.
        </h2>
      </Reveal>

      <Reveal delay={0.2}>
        <p className="text-fwd-mid text-lg md:text-xl max-w-2xl mx-auto leading-relaxed font-light">
          Don&rsquo;t underestimate{" "}
          <Tooltip
            content={
              <div>
                <div className="font-display text-base text-fwd-yellow mb-3">
                  What &ldquo;personalized narratives at scale&rdquo; really
                  takes:
                </div>
                <ul className="space-y-2">
                  {CHALLENGE_ITEMS.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2 text-xs leading-relaxed text-white/85"
                    >
                      <span className="text-fwd-red mt-[0.35em] shrink-0">
                        &bull;
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-4 pt-3 border-t border-white/15 text-[0.7rem] text-white/55 leading-relaxed">
                  We&rsquo;ve already built all of this. You get the finished
                  product.
                </div>
              </div>
            }
          >
            the challenge
          </Tooltip>{" "}
          of high-quality personalized narratives. Teams that build this from
          scratch spend their time on plumbing. We&rsquo;ve already built it, so
          you can focus on your audience.
        </p>
      </Reveal>

      <Reveal delay={0.3}>
        <div className="mt-10 text-xs text-fwd-mid/60 tracking-wide uppercase">
          <span aria-hidden="true" className="mr-1">
            &#x2197;
          </span>
          Open &ldquo;the challenge&rdquo; above to peek behind the curtain
        </div>
      </Reveal>
    </div>
  </section>
);
